import {useEffect} from 'react';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$createParagraphNode, $getRoot} from 'lexical';

import {
  $createPageNode,
  $createPageNodeWithHeaderFooter,
  $isPageNode,
  PageNode,
} from '../../nodes/PageNode';
import {$isPageBreakNode} from '../../nodes/PageBreakNode';

function getTypeSafe(node) {
  return typeof node?.getType === 'function' ? node.getType() : null;
}

function isRegionNodeType(nodeType) {
  return nodeType === 'page_header' || nodeType === 'page_body' || nodeType === 'page_footer';
}

function ensureNonEmptyRegion(region) {
  if (!region) return;
  if (typeof region.getChildrenSize === 'function' && region.getChildrenSize() === 0) {
    // Keep it minimal; Lexical will render an empty paragraph as a caret target.
    region.append($createParagraphNode());
  }
}

/**
 * Removes any region nodes that have been accidentally nested inside the body.
 * These cause duplicate header/footer visual artifacts.
 * Content from nested regions is unwrapped and kept in the body.
 */
function cleanupNestedRegionsInBody(body) {
  if (!body) return;
  
  const children = body.getChildren();
  for (const child of children) {
    const childType = getTypeSafe(child);
    if (isRegionNodeType(childType)) {
      // Unwrap the region: move its children to where the region was, then remove it
      const regionChildren = child.getChildren();
      for (const rc of regionChildren) {
        rc.remove();
        child.insertBefore(rc);
      }
      child.remove();
    }
  }
}

function normalizePageRegions(page) {
  const kids = page.getChildren();

  const headers = [];
  const bodies = [];
  const footers = [];
  const others = [];

  for (const k of kids) {
    const t = getTypeSafe(k);
    if (t === 'page_header') headers.push(k);
    else if (t === 'page_body') bodies.push(k);
    else if (t === 'page_footer') footers.push(k);
    else others.push(k);
  }

  let header = headers[0] ?? null;
  let body = bodies[0] ?? null;
  let footer = footers[0] ?? null;

  // If the page has no recognizable regions, treat all content as body content.
  // BUT we must not move existing region nodes into the body - that causes duplicates.
  if (!header || !body || !footer) {
    const newPage = $createPageNode();
    const newBody = typeof newPage.getBody === 'function' ? newPage.getBody() : null;
    const newHeader = typeof newPage.getHeader === 'function' ? newPage.getHeader() : null;
    const newFooter = typeof newPage.getFooter === 'function' ? newPage.getFooter() : null;
    
    if (newBody) {
      for (const k of kids) {
        const kType = getTypeSafe(k);
        
        // If this is a region node, handle it specially
        if (kType === 'page_header') {
          // Move header content to the new header, discard the old region node
          if (newHeader) {
            const headerKids = k.getChildren();
            for (const hk of headerKids) {
              hk.remove();
              newHeader.append(hk);
            }
          }
          k.remove();
        } else if (kType === 'page_body') {
          // Move body content to the new body, discard the old region node
          const bodyKids = k.getChildren();
          for (const bk of bodyKids) {
            bk.remove();
            newBody.append(bk);
          }
          k.remove();
        } else if (kType === 'page_footer') {
          // Move footer content to the new footer, discard the old region node
          if (newFooter) {
            const footerKids = k.getChildren();
            for (const fk of footerKids) {
              fk.remove();
              newFooter.append(fk);
            }
          }
          k.remove();
        } else {
          // Non-region content goes into body
          k.remove();
          newBody.append(k);
        }
      }
    }
    page.replace(newPage);
    return;
  }

  // Merge duplicated regions into the primary one.
  for (let i = 1; i < headers.length; i++) {
    const extra = headers[i];
    const moving = extra.getChildren();
    for (const c of moving) {
      c.remove();
      header.append(c);
    }
    extra.remove();
  }
  for (let i = 1; i < bodies.length; i++) {
    const extra = bodies[i];
    const moving = extra.getChildren();
    for (const c of moving) {
      c.remove();
      body.append(c);
    }
    extra.remove();
  }
  for (let i = 1; i < footers.length; i++) {
    const extra = footers[i];
    const moving = extra.getChildren();
    for (const c of moving) {
      c.remove();
      footer.append(c);
    }
    extra.remove();
  }

  // Move any non-region direct children into the body.
  for (const n of others) {
    n.remove();
    body.append(n);
  }

  // Clean up any region nodes that accidentally ended up nested inside the body.
  // This can happen due to paste, import, or edge cases in content manipulation.
  cleanupNestedRegionsInBody(body);

  // Ensure each region has at least one child for caret stability.
  ensureNonEmptyRegion(header);
  ensureNonEmptyRegion(body);
  ensureNonEmptyRegion(footer);

  // Ensure exact [header, body, footer] order as direct children.
  const finalKids = page.getChildren();
  const isOrdered =
    finalKids.length === 3 &&
    getTypeSafe(finalKids[0]) === 'page_header' &&
    getTypeSafe(finalKids[1]) === 'page_body' &&
    getTypeSafe(finalKids[2]) === 'page_footer';

  if (!isOrdered) {
    const replacement = new PageNode();
    // Preserve formatting metadata where possible
    if (typeof page.getFormat === 'function') replacement.setFormat(page.getFormat());
    if (typeof page.getIndent === 'function') replacement.setIndent(page.getIndent());
    if (typeof page.getDirection === 'function') replacement.setDirection(page.getDirection());

    header.remove();
    body.remove();
    footer.remove();
    replacement.append(header);
    replacement.append(body);
    replacement.append(footer);

    // Clean up ALL remaining children before replacement - this prevents orphaned
    // region nodes that cause duplicate header/footer artifacts.
    const leftovers = page.getChildren();
    for (const n of leftovers) {
      const t = getTypeSafe(n);
      if (isRegionNodeType(t)) {
        // Orphaned/duplicate region node - remove it completely
        n.remove();
      } else {
        // Non-region content goes into body
        n.remove();
        body.append(n);
      }
    }

    page.replace(replacement);
  }
}

function pageHasThreeRegions(page) {
  return page.getChildrenSize() === 3;
}

function getBody(page) {
  return typeof page.getBody === 'function' ? page.getBody() : null;
}

function splitOnManualPageBreaks(root, pages) {
  for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
    const page = pages[pageIndex];
    const body = getBody(page);
    if (!body) continue;

    const bodyChildren = body.getChildren();
    for (let i = 0; i < bodyChildren.length; i++) {
      const child = bodyChildren[i];
      if (!$isPageBreakNode(child)) continue;

      // Old pagination used auto PageBreakNodes. When switching to PageNode pagination,
      // those must be discarded (otherwise they generate blank pages on refresh).
      if (typeof child.isAuto === 'function' && child.isAuto()) {
        child.remove();
        continue;
      }

      // Copy header/footer from the current page
      const nextPage = $createPageNodeWithHeaderFooter(page);
      page.insertAfter(nextPage);

      const nextBody = getBody(nextPage);
      if (nextBody) {
        const after = child.getNextSibling();
        while (after) {
          const move = after;
          const next = after.getNextSibling();
          move.remove();
          nextBody.append(move);
          after = next;
        }
      }

      child.remove();

      // Refresh pages array after insertion.
      pages = root.getChildren().filter($isPageNode);
      // Continue scanning from the newly created page.
      break;
    }
  }

  return pages;
}

export default function EnsurePagedRootPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerUpdateListener(({tags}) => {
      if (tags != null && tags.has('paged-root')) return;

      editor.update(
        () => {
          const root = $getRoot();
          const children = root.getChildren();

          let pages = children.filter($isPageNode);

          if (pages.length === 0) {
            root.append($createPageNode());
            pages = root.getChildren().filter($isPageNode);
          }

          for (const p of pages) {
            // Normalize bad/legacy structures created by earlier PageNode import behavior.
            // This keeps refresh deterministic and prevents content being pushed to later pages.
            normalizePageRegions(p);
          }

          pages = root.getChildren().filter($isPageNode);
          const first = pages[0];
          const body = getBody(first);
          if (!body) return;

          const rootNow = root.getChildren();
          for (const n of rootNow) {
            if (!$isPageNode(n)) {
              const t = getTypeSafe(n);
              // Orphaned region nodes (header/body/footer) should never exist at root.
              // If they do, dropping them prevents "duplicate header/footer" artifacts.
              if (isRegionNodeType(t)) {
                n.remove();
                continue;
              }

              n.remove();
              body.append(n);
            }
          }

          pages = root.getChildren().filter($isPageNode);
          splitOnManualPageBreaks(root, pages);
        },
        {tag: 'paged-root'},
      );
    });
  }, [editor]);

  return null;
}
